import { memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClock,
  faTruck,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { getTimeAgo, isDateInPast } from "../../utils/dateUtils";
import "../../style/RequestCardCompact.css";

// status config: label, accent color
const getStatusInfo = (request, t) => {
  if (!request.active) {
    return { label: t("Inactive"), color: "#6b7280" };
  }
  if (request.date_delivered) {
    return { label: t("Delivered"), color: "#6b7280", icon: faTruck };
  }
  if (request.pickup_confirmed === 1) {
    return { label: t("Awaiting Pickup"), color: "#2196F3", icon: faClock };
  }
  if (request.date_allocated) {
    return { label: t("Approved"), color: "#10b981", icon: faCheckCircle };
  }
  if (request.scheduled_date && isDateInPast(request.scheduled_date)) {
    return { label: t("Expired"), color: "#6b7280" };
  }
  return { label: t("Pending"), color: "#f59e0b" };
};

// format date as "Jan 15, 2027"
const formatShortDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const [year, month, day] = dateString.split(/[-T]/);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

// days until scheduled pickup
const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  try {
    const [year, month, day] = dateString.split(/[-T]/);
    const target = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / 86400000);
  } catch {
    return null;
  }
};

function RequestCardCompact({ request, darkMode, onClick }) {
  const { t } = useTranslation();

  const status = getStatusInfo(request, t);
  const timeAgo = getTimeAgo(request.date_modified || request.date_created);
  const daysLeft = getDaysUntil(request.scheduled_date);
  const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;
  const isOverdue = daysLeft !== null && daysLeft < 0 && !request.date_allocated;
  const isPending = !request.date_allocated && request.active && !(request.scheduled_date && isDateInPast(request.scheduled_date));

  return (
    <div
      className={`req-card ${darkMode ? "dark" : "light"} ${isPending ? "req-card--pending" : ""}`}
      onClick={() => onClick?.(request)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(request);
        }
      }}
    >
      {/* left accent bar */}
      <div
        className="req-card__accent"
        style={{ backgroundColor: status.color }}
      />

      <div className="req-card__body">
        {/* top row: material + item name as title, status badge */}
        <div className="req-card__header">
          <div className="req-card__title-group">
            <span className="req-card__material-title">
              {request.material || "—"}
              {request.item_name && (
                <span className="req-card__item-name"> · {request.item_name}</span>
              )}
            </span>
            <span className="req-card__requester-sub">
              {request.receiver_company || request.client_id || "—"}
            </span>
          </div>
          <span
            className="req-card__status"
            style={{
              color: status.color,
              borderColor: `${status.color}40`,
              backgroundColor: `${status.color}12`,
            }}
          >
            {status.icon && <FontAwesomeIcon icon={status.icon} className="req-card__status-icon" />}
            {status.label}
          </span>
        </div>

        {/* middle row: key metrics */}
        <div className="req-card__metrics">
          <div className="req-card__metric">
            <span className="req-card__metric-label">{t("Quantity Range")}</span>
            <span className="req-card__metric-value">
              {request.date_allocated
                ? `${request.quantity || 0} lbs`
                : `${request.min_qty || 0} – ${request.max_qty || 0} lbs`}
            </span>
          </div>
          <div className="req-card__metric">
            <span className="req-card__metric-label">{t("Pickup Date")}</span>
            <span className={`req-card__metric-value ${isOverdue ? "overdue" : ""}`}>
              {formatShortDate(request.scheduled_date)}
              {isUrgent && (
                <span className="req-card__urgency">
                  {daysLeft === 0 ? t("Today") : `${daysLeft}d`}
                </span>
              )}
              {isOverdue && (
                <span className="req-card__urgency overdue">{t("Overdue")}</span>
              )}
            </span>
          </div>
        </div>

        {/* bottom row: meta + arrow */}
        <div className="req-card__footer">
          <div className="req-card__meta">
            {timeAgo && (
              <span className="req-card__time">
                <FontAwesomeIcon icon={faClock} />
                {timeAgo}
              </span>
            )}
          </div>
          <span className="req-card__arrow">
            {t("View Details")} <FontAwesomeIcon icon={faArrowRight} />
          </span>
        </div>
      </div>
    </div>
  );
}

RequestCardCompact.propTypes = {
  request: PropTypes.object.isRequired,
  darkMode: PropTypes.bool,
  onClick: PropTypes.func,
};

export default memo(RequestCardCompact);
